import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, InputGroup, Modal } from "react-bootstrap";
import {
  FaEdit,
  FaEllipsisV,
  FaFlag,
  FaHeart,
  FaRegHeart,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import CommentForm from "../CommentForm";
import Pagination from "../Pagination";
import { processCommentText, timeSince } from "./commentUtils";
import SupporterName from './SupporterName';

const AttachedCardList = ({ cards }) => (
  <div className="d-flex flex-wrap">
    {cards.map((attachedCard, index) => (
      <img
        key={attachedCard.cardid ?? attachedCard.id ?? index}
        className="img-fluid"
        src={`/cards/${attachedCard.cardid}.webp`}
        alt={`Card ${index + 1}`}
      />
    ))}
  </div>
);

const CommentEmptyState = ({ hasActiveSearch }) => (
  <div className="text-center text-white mt-4 mb-4">
    <img
      src="https://www.hearthcards.net/images/ded-murloc-phone21.svg"
      alt="Murloc"
      style={{ width: "180px" }}
      className="mb-3"
    />
    {hasActiveSearch ? (
      <>
        <h5 className="fw-bold">No results found</h5>
        <p>Your search didn&apos;t match any comments.</p>
      </>
    ) : (
      <>
        <h5 className="fw-bold">Be the first to comment</h5>
        <p>Nobody&apos;s commented on this card yet.</p>
      </>
    )}
  </div>
);

const CommentItem = ({
  activeSearchTerm,
  cardInfo,
  comment,
  editedCommentText,
  editingCommentId,
  onCancelEdit,
  onDelete,
  onEdit,
  onEditedTextChange,
  onLike,
  onModeratorRemove,
  onReply,
  onReport,
  onSave,
  targetCommentId,
  targetCommentRef,
  user,
}) => {
  const isEditing = Number(editingCommentId) === Number(comment.id);
  const isCommentOwner = user && Number(user.userid) === Number(comment.userid);
  const isCardAuthor = Number(comment.userid) === Number(cardInfo.creatorId);

  return (
    <div
      id={`comment-${comment.id}`}
      ref={
        Number(comment.id) === Number(targetCommentId) ? targetCommentRef : null
      }
      className={`comment ${Number(comment.sticky) === 1 ? "comment-sticky mb-3" : "mb-2"}`}
    >
      <div className="row" style={{ alignItems: "center" }}>
        {comment.userpicture ? (
          <>
            <div className="col-auto pe-2" style={{ paddingRight: 0 }}>
              <Link
                to={`/gallery/user/${comment.userid}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <img
                  src={
                    comment.userpicture.startsWith("http")
                      ? comment.userpicture
                      : `/images/users/pictures/${comment.userpicture}`
                  }
                  alt={`${comment.username}'s profile`}
                  className="img-fluid rounded-circle"
                  style={{ width: "40px", height: "40px" }}
                />
              </Link>
            </div>
            <div className="col" style={{ paddingLeft: 0 }}>
              <div className="row">
                <div className="col text-start">
                  <strong>
<Link
  to={`/gallery/user/${comment.userid}`}
  className="comment-username-link"
>
  <SupporterName
    name={comment.username}
    supColor={comment.supColor}
  />

  {isCardAuthor && (
    <span className="creator-tag">
      {' '}
      (author)
    </span>
  )}
</Link>
                  </strong>
                  {comment.supIcon && (
                    <img
                      src={`https://www.hearthcards.net/images/emotes/${comment.supIcon}.png`}
                      alt="Supporter Icon"
                      className="ps-1"
                      style={{ maxHeight: "22px", marginTop: "-2px" }}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col text-start">{timeSince(comment.date)}</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="col text-start">
              <strong>
                <Link
                  to={`/gallery/user/${comment.userid}`}
                  style={{
                    textDecoration: "none",
                    color: comment.supColor || "inherit",
                  }}
                >
                  {comment.username}
                  {isCardAuthor && (
                    <span className="creator-tag"> (author)</span>
                  )}
                </Link>
              </strong>
            </div>
            <div className="col-auto text-end">{timeSince(comment.date)}</div>
          </>
        )}
      </div>

      <div className="row">
        <div className="col text-start">
          {isEditing ? (
            <textarea
              className="form-control"
              value={editedCommentText}
              onChange={(event) => onEditedTextChange(event.target.value)}
              rows="3"
            />
          ) : (
            <span
              className="comment-text"
              dangerouslySetInnerHTML={{
                __html: processCommentText(comment.comment, activeSearchTerm),
              }}
            />
          )}
        </div>

        {comment.tokens?.length > 0 && (
          <div className="attached-cards my-1 d-flex">
            <AttachedCardList cards={comment.tokens} />
          </div>
        )}
      </div>

      {isCommentOwner && isEditing && (
        <div className="row py-2">
          <div className="col text-end d-flex justify-content-end">
            <button
              className="btn btn-link me-1 comment-cancel-button"
              onClick={onCancelEdit}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary comment-save-button"
              onClick={() => onSave(comment.id)}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {!isEditing && (
        <div className="row">
          <div className="col text-end d-flex align-items-center">
            <button
              className="btn btn-link p-0 me-2 comment-like-button text-decoration-none"
              onClick={() => onLike(comment.id)}
            >
              {comment.liked ? <FaHeart color="red" /> : <FaRegHeart />}{" "}
              {comment.likes > 0 && comment.likes}
            </button>

            {user && !isCommentOwner && (
              <button
                className="btn btn-link p-0 me-2 comment-edit-button align-item-end"
                onClick={() => onReply(comment.id)}
              >
                Reply
              </button>
            )}
          </div>

          <div className="col d-flex justify-content-end">
            <Dropdown>
              <Dropdown.Toggle
                as="button"
                className="btn btn-link p-0 comment-options-button text-decoration-none"
              >
                <FaEllipsisV />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {user && !isCommentOwner && (
                  <Dropdown.Item
                    onClick={() => onReport("comment", comment.id)}
                  >
                    <FaFlag className="me-2" /> Report
                  </Dropdown.Item>
                )}

                {isCommentOwner && (
                  <>
                    <Dropdown.Item onClick={() => onEdit(comment.id)}>
                      <FaEdit className="me-2" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onDelete(comment.id)}>
                      <FaTrash className="me-2" /> Delete
                    </Dropdown.Item>
                  </>
                )}

                {user?.mod === true && !isCommentOwner && (
                  <Dropdown.Item
                    onClick={() => onModeratorRemove(comment.id)}
                    style={{ color: "red" }}
                  >
                    <FaShieldAlt className="me-2" color="green" /> Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      )}
    </div>
  );
};

const CommentsSection = ({
  cardInfo,
  onModeratorRemove,
  onReport,
  onTotalCommentsChange,
  user,
}) => {
  const [comments, setComments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [commentSearchQuery, setCommentSearchQuery] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [showCommentDeleteModal, setShowCommentDeleteModal] = useState(false);
  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [targetCommentId, setTargetCommentId] = useState(
    searchParams.get("commentId"),
  );

  const location = useLocation();
  const searchInputRef = useRef(null);
  const lastFetchedPageRef = useRef(null);
  const lastFetchedSearchRef = useRef(null);
  const targetCommentIdRef = useRef(targetCommentId);
  const targetCleanupRef = useRef(null);

  useEffect(() => {
    targetCommentIdRef.current = targetCommentId;
  }, [targetCommentId]);

  useEffect(
    () => () => {
      targetCleanupRef.current?.();
    },
    [],
  );

  const targetCommentRef = useCallback(
    (node) => {
      if (!node) {
        targetCleanupRef.current?.();
        targetCleanupRef.current = null;
        return;
      }

      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("highlight-comment");

      const scrollContainer = node.closest(".card-modal-body") || document.body;
      const resizeObserver = new ResizeObserver(() => {
        node.scrollIntoView({ behavior: "auto", block: "center" });
      });
      resizeObserver.observe(scrollContainer);

      const stopTracking = () => {
        resizeObserver.disconnect();
        scrollContainer.removeEventListener("wheel", stopTracking);
        scrollContainer.removeEventListener("touchstart", stopTracking);
        scrollContainer.removeEventListener("mousedown", stopTracking);
        scrollContainer.removeEventListener("keydown", stopTracking);
      };

      scrollContainer.addEventListener("wheel", stopTracking, {
        passive: true,
      });
      scrollContainer.addEventListener("touchstart", stopTracking, {
        passive: true,
      });
      scrollContainer.addEventListener("mousedown", stopTracking, {
        passive: true,
      });
      scrollContainer.addEventListener("keydown", stopTracking, {
        passive: true,
      });

      const timerId = window.setTimeout(() => {
        stopTracking();
        setTargetCommentId(null);
        targetCommentIdRef.current = null;
        setSearchParams(
          (previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.delete("commentId");
            return nextParams;
          },
          { replace: true, preventScrollReset: true },
        );
      }, 3000);

      targetCleanupRef.current = () => {
        window.clearTimeout(timerId);
        stopTracking();
      };
    },
    [setSearchParams],
  );

  const fetchComments = useCallback(
    async (page, search = "", force = false) => {
      if (
        !force &&
        lastFetchedPageRef.current === page &&
        lastFetchedSearchRef.current === search
      ) {
        return;
      }

      setIsLoadingComments(true);
      try {
        const response = await fetch(
          `/api/comments/comments-stuff.php?id=${cardInfo.cardId}&page=${page}&search=${encodeURIComponent(
            search,
          )}&target_comment=${targetCommentIdRef.current || ""}`,
        );
        const data = await response.json();
        const serverPage = data.main.current_page;

        lastFetchedSearchRef.current = search;
        if (serverPage && serverPage !== page) {
          lastFetchedPageRef.current = serverPage;
          setCurrentPage(serverPage);
        } else {
          lastFetchedPageRef.current = page;
        }

        setComments(data.comments);
        setTotalPages(data.main.maxpage);
        onTotalCommentsChange?.(data.main.total);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      } finally {
        setIsLoadingComments(false);
      }
    },
    [cardInfo.cardId, onTotalCommentsChange],
  );

  useEffect(() => {
    lastFetchedPageRef.current = null;
    lastFetchedSearchRef.current = null;
    setComments([]);
    setEditingCommentId(null);
    setReplyingCommentId(null);
    onTotalCommentsChange?.(0);
  }, [cardInfo.cardId, onTotalCommentsChange]);

  useEffect(() => {
    fetchComments(currentPage, activeSearchTerm);
  }, [activeSearchTerm, currentPage, fetchComments]);

  useEffect(() => {
    if (isSearchVisible) searchInputRef.current?.focus();
  }, [isSearchVisible]);

  useEffect(() => {
    const page = Number.parseInt(location.hash.replace("#", ""), 10);
    setCurrentPage(Number.isNaN(page) || page <= 0 ? 1 : page);
  }, [location.hash]);

  const refreshFirstPage = useCallback(() => {
    if (currentPage === 1) {
      fetchComments(1, activeSearchTerm, true);
    } else {
      lastFetchedPageRef.current = null;
      setCurrentPage(1);
    }
  }, [activeSearchTerm, currentPage, fetchComments]);

  const handleSearchTrigger = () => {
    setActiveSearchTerm(commentSearchQuery);
    if (currentPage !== 1) setCurrentPage(1);
    searchInputRef.current?.blur();
  };

  const handleClearSearch = () => {
    setIsSearchVisible(false);
    setCommentSearchQuery("");
    if (activeSearchTerm) {
      setActiveSearchTerm("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await axios.post("/api/comments/like-a-comment.php", {
        commentId,
      });
      setComments((previousComments) =>
        previousComments.map((comment) =>
          Number(comment.id) === Number(commentId)
            ? {
                ...comment,
                likes: response.data.likes,
                liked: response.data.liked,
              }
            : comment,
        ),
      );
    } catch {
      alert("An error occurred while liking the comment");
    }
  };

  const handleEditComment = (commentId) => {
    const comment = comments.find(
      (item) => Number(item.id) === Number(commentId),
    );
    if (!comment) return;

    setEditingCommentId(Number(commentId));
    setEditedCommentText(comment.comment);
  };

  const handleSaveComment = async (commentId) => {
    try {
      const response = await axios.post("/api/comments/update-comment.php", {
        id: commentId,
        text: editedCommentText,
      });

      if (!response.data.success) {
        alert("Failed to update comment");
        return;
      }

      setComments((previousComments) =>
        previousComments.map((comment) =>
          Number(comment.id) === Number(commentId)
            ? { ...comment, comment: editedCommentText }
            : comment,
        ),
      );
      setEditingCommentId(null);
      setEditedCommentText("");
    } catch {
      alert("An error occurred while updating the comment");
    }
  };

  const openDeleteCommentModal = (commentId) => {
    setDeletingCommentId(Number(commentId));
    setShowCommentDeleteModal(true);
  };

  const handleDeleteCommentRequest = async () => {
    try {
      const response = await axios.post("/api/comments/update-comment.php", {
        id: deletingCommentId,
        text: "",
      });

      if (!response.data.success) {
        alert("Failed to delete comment");
        return;
      }

      setComments((previousComments) =>
        previousComments.filter(
          (comment) => Number(comment.id) !== Number(deletingCommentId),
        ),
      );
      setDeletingCommentId(null);
      setShowCommentDeleteModal(false);
      onTotalCommentsChange?.((currentTotal) => Math.max(0, currentTotal - 1));
    } catch {
      alert("An error occurred while deleting the comment");
    }
  };

  return (
    <>
      {user && (
        <CommentForm cardInfo={cardInfo} onCommentSubmit={refreshFirstPage} />
      )}

      <h6 className="mt-3 commentsHeader">
        Comments
        {!isSearchVisible && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setIsSearchVisible(true)}
            style={{ border: "none", backgroundColor: "unset" }}
          >
            <FaSearch />
          </Button>
        )}
      </h6>

      {isSearchVisible && (
        <InputGroup className="mb-3">
          <Form.Control
            ref={searchInputRef}
            placeholder="Search comments..."
            aria-label="Search comments"
            value={commentSearchQuery}
            onChange={(event) => setCommentSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSearchTrigger();
            }}
          />
          <Button variant="secondary" onClick={handleClearSearch}>
            <FaTimes />
          </Button>
        </InputGroup>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          useHash
          arrowFlag={false}
        />
      )}

      {isLoadingComments ? (
        <div className="text-center my-4">
          <p className="text-muted">Loading comments...</p>
        </div>
      ) : comments.length === 0 ? (
        <CommentEmptyState hasActiveSearch={Boolean(activeSearchTerm)} />
      ) : (
        comments.map((comment) => (
          <React.Fragment key={comment.id}>
            <CommentItem
              activeSearchTerm={activeSearchTerm}
              cardInfo={cardInfo}
              comment={comment}
              editedCommentText={editedCommentText}
              editingCommentId={editingCommentId}
              onCancelEdit={() => setEditingCommentId(null)}
              onDelete={openDeleteCommentModal}
              onEdit={handleEditComment}
              onEditedTextChange={setEditedCommentText}
              onLike={handleLikeComment}
              onModeratorRemove={onModeratorRemove}
              onReply={(commentId) => setReplyingCommentId(Number(commentId))}
              onReport={onReport}
              onSave={handleSaveComment}
              targetCommentId={targetCommentId}
              targetCommentRef={targetCommentRef}
              user={user}
            />

            {replyingCommentId === Number(comment.id) && (
              <div className="mb-3">
                <CommentForm
                  cardInfo={cardInfo}
                  onCommentSubmit={() => {
                    refreshFirstPage();
                    setReplyingCommentId(null);
                  }}
                  initialComment={`@${comment.username}, `}
                  commentReplyId={replyingCommentId}
                  isReply
                />
              </div>
            )}
          </React.Fragment>
        ))
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          useHash
          arrowFlag={false}
        />
      )}

      <Modal
        show={showCommentDeleteModal}
        onHide={() => setShowCommentDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this comment?</p>
          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              onClick={() => setShowCommentDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ms-2"
              onClick={handleDeleteCommentRequest}
            >
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CommentsSection;
